/**
 * journeyStore — keeps a "Record a journey" alive across full page loads in the
 * same browser tab (kit rule, 16 Oct 2026: a journey recording belongs to the
 * tab, not the page). A plain link, location.assign or a reload used to end the
 * recording and drop everything captured so far. The click/screen timeline,
 * one screenshot per page and the voice pieces now live in IndexedDB, with a
 * sessionStorage tail for the last moments before a page goes, and a
 * sessionStorage flag tells the Reporter on the next page to carry on.
 *
 * Voice is recorded per page in one-second pieces. On Stop the pieces of each
 * page take are joined, and several takes are merged into one WAV with each
 * take placed at its real time, so the voice lines up with the timeline.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
const FLAG = 'vault-rec';
const TAIL = 'vault-rec-tail';
const DB = 'vault-journey';
const MAX_AGE_MS = 60 * 60 * 1000;

/** The recording in progress in this tab: {start, voice, tab, pages}, or null.
 * A flag older than an hour is treated as abandoned. */
export function activeJourney() {
  try {
    const f = JSON.parse(window.sessionStorage.getItem(FLAG) || 'null');
    if (!f || !f.start || Date.now() - f.start > MAX_AGE_MS) return null;
    return f;
  } catch { return null; }
}

/** Set (or with null, clear) the tab's recording flag. */
export function markJourney(f) {
  try {
    if (f) window.sessionStorage.setItem(FLAG, JSON.stringify(f));
    else { window.sessionStorage.removeItem(FLAG); window.sessionStorage.removeItem(TAIL); }
  } catch { /* this page still records; the next page just won't pick it up */ }
}

/** Open (and on first use create) the tab's journey database. */
function openDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) { reject(new Error('no indexeddb')); return; }
    const req = window.indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const s of ['events', 'audio', 'shots']) {
        if (!db.objectStoreNames.contains(s)) db.createObjectStore(s, { autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

let dbp = null;
/** One shared connection per page; a failed open is retried on the next call. */
function db() {
  if (!dbp) dbp = openDb().catch((e) => { dbp = null; throw e; });
  return dbp;
}

/** Run fn against one object store and resolve when the transaction commits. */
function tx(store, mode, fn) {
  return db().then((d) => new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const out = fn(t.objectStore(store));
    t.oncomplete = () => resolve(out && out.result !== undefined ? out.result : undefined);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

/** Start clean: nothing carried over from an earlier recording. */
export function clearJourney() {
  try { window.sessionStorage.removeItem(TAIL); } catch { /* ignore */ }
  return Promise.all(['events', 'audio', 'shots'].map(
    (s) => tx(s, 'readwrite', (st) => st.clear()),
  )).catch(() => {});
}

/** Append a batch of timeline events ({t, type, ...}). */
export function saveEvents(batch) {
  if (!batch.length) return Promise.resolve();
  return tx('events', 'readwrite', (s) => { s.add(batch); }).catch(() => {});
}

/** The page is going: sessionStorage is written at once, IndexedDB may not finish. */
export function saveTail(batch) {
  if (!batch.length) return;
  try {
    const old = JSON.parse(window.sessionStorage.getItem(TAIL) || '[]');
    window.sessionStorage.setItem(TAIL, JSON.stringify(old.concat(batch)));
  } catch { /* too big for sessionStorage: those last events are lost */ }
}

/** Fold the previous page's tail into IndexedDB once the new page is up. */
export function absorbTail() {
  let tail = [];
  try { tail = JSON.parse(window.sessionStorage.getItem(TAIL) || '[]'); } catch { tail = []; }
  try { window.sessionStorage.removeItem(TAIL); } catch { /* ignore */ }
  return saveEvents(tail);
}

/** Everything on the timeline so far, oldest first, tail included. */
export function loadEvents() {
  return tx('events', 'readonly', (s) => s.getAll()).then((batches) => {
    const out = [].concat(...(batches || []));
    try {
      const tail = JSON.parse(window.sessionStorage.getItem(TAIL) || '[]');
      // The tail is only written when IndexedDB may not have finished, so it
      // can overlap what was flushed; keep only what is newer.
      const last = out.length ? out[out.length - 1].t : -1;
      for (const ev of tail) if (ev.t > last) out.push(ev);
    } catch { /* no tail */ }
    out.sort((a, b) => a.t - b.t);
    return out;
  }).catch(() => []);
}

/** One screenshot per page: page = 1-based page number, t = ms after the start. */
export function saveShot(page, t, blob) {
  if (!blob) return Promise.resolve();
  return tx('shots', 'readwrite', (s) => { s.add({ page, t, blob }); }).catch(() => {});
}

/** The page screenshots, in page order. */
export function loadShots() {
  return tx('shots', 'readonly', (s) => s.getAll())
    .then((rows) => (rows || []).sort((a, b) => a.t - b.t))
    .catch(() => []);
}

/** One voice piece: segment = one page's microphone take, offset = ms after the
 * journey began when that take started. */
export function saveAudioChunk(segment, offset, blob, type) {
  return tx('audio', 'readwrite', (s) => { s.add({ segment, offset, blob, type }); }).catch(() => {});
}

/** The voice as one file lined up with the journey: {blob, offset} or null.
 * A single take goes as recorded; several are merged into one WAV. */
export async function loadAudio() {
  let rows = [];
  try { rows = (await tx('audio', 'readonly', (s) => s.getAll())) || []; } catch { return null; }
  if (!rows.length) return null;
  const segs = new Map();
  for (const r of rows) {
    if (!segs.has(r.segment)) segs.set(r.segment, { offset: r.offset, type: r.type, parts: [] });
    segs.get(r.segment).parts.push(r.blob);
  }
  const list = [...segs.values()].map((s) => ({
    offset: s.offset, blob: new Blob(s.parts, { type: s.type || 'audio/webm' }),
  }));
  list.sort((a, b) => a.offset - b.offset);
  if (list.length === 1) return list[0];
  try { return await mergeToWav(list); } catch { return list[0]; }
}

const RATE = 16000;

/** Several pages' voice takes become one mono WAV, each placed at its own time
 * with silence between, so the voice stays in step with the timeline. */
async function mergeToWav(list) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const ctx = new Ctx();
  const decoded = [];
  for (const s of list) {
    try {
      const buf = await ctx.decodeAudioData(await s.blob.arrayBuffer());
      decoded.push({ offset: s.offset, buf });
    } catch { /* a take that won't decode is left out rather than sinking the rest */ }
  }
  try { ctx.close(); } catch { /* ignore */ }
  if (!decoded.length) throw new Error('nothing decoded');
  const start = decoded[0].offset;
  const endMs = Math.max(...decoded.map((d) => d.offset - start + d.buf.duration * 1000));
  const length = Math.max(1, Math.ceil((endMs / 1000) * RATE));
  const off = new OfflineAudioContext(1, length, RATE);
  for (const d of decoded) {
    const src = off.createBufferSource();
    src.buffer = d.buf;
    src.connect(off.destination);
    src.start((d.offset - start) / 1000);
  }
  const out = await off.startRendering();
  return { offset: start, blob: wav(out.getChannelData(0)) };
}

/** 16-bit PCM WAV from mono float samples. */
function wav(samples) {
  const buf = new ArrayBuffer(44 + samples.length * 2);
  const v = new DataView(buf);
  const str = (o, s) => { for (let i = 0; i < s.length; i += 1) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + samples.length * 2, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, RATE, true); v.setUint32(28, RATE * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, samples.length * 2, true);
  for (let i = 0; i < samples.length; i += 1) {
    const x = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
  }
  return new Blob([buf], { type: 'audio/wav' });
}
